// Builds one claim's whole life backwards from its state (rcm-31 §3): the plan
// for a state is a list of events at day offsets from the visit; the visit is
// then placed so the LAST event lands on or before today (Oct 18, 2026).
// Deterministic: everything is seeded off the claim id. Realistic fees and
// coverage levels (rcm-31 §4), payer-style member IDs, code combinations a
// dentist would bill together, clock times spread by a hash of the id.
import { rng, pick, int, usd, FIRST_NAMES, LAST_NAMES } from '@/components/lists';
import type {
  BandSentence,
  ClaimLink,
  ClaimStatus,
  HistoryClaim,
  HistoryProcedure,
  OpenLink,
  PathStep,
  PaymentLine,
  TimelineEntry,
  Tone,
} from './historyMock';
import { CLAIMS_TODAY, CLAIMS_TODAY_MAX_MIN } from './today';
import { PMS } from '@/features/shared/pms';

// ---------------------------------------------------------------- dates ----

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function parseIso(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y ?? 2026, (m ?? 1) - 1, d ?? 1);
}
export function isoOf(dt: Date): string {
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${String(dt.getFullYear())}-${m}-${d}`;
}
/** "Oct 12", or "Oct 12, 2025" when not in the demo year. */
export function shortOf(dt: Date): string {
  const base = `${MONTHS[dt.getMonth()] ?? 'Jan'} ${String(dt.getDate())}`;
  return dt.getFullYear() === 2026 ? base : `${base}, ${String(dt.getFullYear())}`;
}
export function fullOf(dt: Date): string {
  return `${MONTHS[dt.getMonth()] ?? 'Jan'} ${String(dt.getDate())}, ${String(dt.getFullYear())}`;
}
export function addDays(dt: Date, n: number): Date {
  const d = new Date(dt);
  d.setDate(d.getDate() + n);
  return d;
}
export function clock12(min: number): string {
  const h24 = Math.floor(min / 60);
  const m = min % 60;
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${String(h)}:${String(m).padStart(2, '0')} ${h24 >= 12 ? 'PM' : 'AM'}`;
}

/** A tiny FNV-1a hash so any id resolves to a stable seed. */
export function hashSeed(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

// ---------------------------------------------------------------- money ----

interface FeeRow {
  desc: string;
  fee: number;
  /** Share the plan pays of the allowed amount: diagnostic/preventive 1, basic 0.8, major 0.5. */
  cov: number;
}
/** The practice fee schedule (rcm-31 §4) and coverage levels. */
export const FEES: Record<string, FeeRow> = {
  D0120: { desc: 'Periodic exam', fee: 62, cov: 1 },
  D0220: { desc: 'Periapical X-ray', fee: 35, cov: 1 },
  D0274: { desc: 'Bitewings, four films', fee: 82, cov: 1 },
  D1110: { desc: 'Adult cleaning', fee: 110, cov: 1 },
  D2392: { desc: 'Resin, two surfaces', fee: 230, cov: 0.8 },
  D2740: { desc: 'Crown, porcelain', fee: 1295, cov: 0.5 },
  D2950: { desc: 'Core buildup', fee: 285, cov: 0.5 },
  D3330: { desc: 'Root canal, molar', fee: 1150, cov: 0.8 },
  D4341: { desc: 'Scaling and root planing', fee: 260, cov: 0.8 },
  D7140: { desc: 'Extraction', fee: 210, cov: 0.8 },
};

export function proc(code: string, tooth: string | null): HistoryProcedure {
  const f = FEES[code];
  return { code, desc: f?.desc ?? code, tooth, fee: f?.fee ?? 0 };
}

const MOLARS = ['#3', '#14', '#19', '#30', '#2', '#18', '#15', '#31'];
const QUADS = ['UR', 'UL', 'LR', 'LL'];

/** Code combinations that go together (rcm-31 §5). */
function procsFor(r: () => number): HistoryProcedure[] {
  const x = r();
  const tooth = pick(r, MOLARS);
  if (x < 0.28) return [proc('D1110', null), proc('D0120', null), proc('D0274', null)];
  if (x < 0.46) return [proc('D2392', tooth), proc('D0220', tooth)];
  if (x < 0.6) return r() < 0.75 ? [proc('D2740', tooth), proc('D2950', tooth)] : [proc('D2740', tooth)];
  if (x < 0.69) {
    const n = int(r, 1, 4);
    return QUADS.slice(0, n).map((q) => proc('D4341', q));
  }
  if (x < 0.77) return [proc('D7140', tooth), proc('D0220', tooth)];
  if (x < 0.84) return [proc('D3330', tooth), proc('D0220', tooth)];
  return [proc('D0120', null), proc('D0220', tooth)];
}

export interface Money {
  billed: number;
  /** What the payer's fee schedule allows. */
  allowed: number;
  /** What the plan is expected to pay. */
  ins: number;
  /** The patient's share of the allowed amount (coverage level). */
  own: number;
  /** Contractual write-off: billed minus allowed. */
  writeOff: number;
}
export function moneyOf(procs: HistoryProcedure[], g: number): Money {
  let billed = 0;
  let allowed = 0;
  let ins = 0;
  for (const p of procs) {
    const line = Math.round(p.fee * g);
    billed += p.fee;
    allowed += line;
    ins += Math.round(line * (FEES[p.code]?.cov ?? 0.8));
  }
  return { billed, allowed, ins, own: allowed - ins, writeOff: billed - allowed };
}

// ---------------------------------------------------------------- people ---

const PERSONA_FIRST = new Set(['Sofia', 'Mateo', 'Jorge']);
const FIRSTS = FIRST_NAMES.filter((n) => !PERSONA_FIRST.has(n));
export const PROVIDERS = ['Dr. Emily Carter', 'Dr. Olivia Brooks', 'Dr. Marcus Reed', 'Dr. Priya Nair'] as const;
const PLAN_SUFFIX = ['PPO', 'DPPO', 'PDP'] as const;
const DIGITS = (r: () => number, n: number): string =>
  Array.from({ length: n }, () => String(int(r, 0, 9))).join('');

/** Payer-style member IDs, never ###-##-#### (rcm-31 §5). */
export function memberIdFor(payer: string, r: () => number): string {
  switch (payer) {
    case 'Aetna': return `W${DIGITS(r, 9)}`;
    case 'Cigna': return `U${DIGITS(r, 4)}-${DIGITS(r, 4)}`;
    case 'Delta Dental': return `DD-${DIGITS(r, 4)}-${DIGITS(r, 4)}`;
    case 'MetLife': return `ML${DIGITS(r, 9)}`;
    case 'UnitedHealthcare': return `UH${DIGITS(r, 9)}`;
    case 'Guardian': return `GU-${DIGITS(r, 4)}-${DIGITS(r, 4)}`;
    case 'Sun Life': return `SL-${DIGITS(r, 4)}-${DIGITS(r, 4)}`;
    case 'BCBS of Texas': return `XTX${DIGITS(r, 9)}`;
    case 'Humana': return `H${DIGITS(r, 8)}`;
    default: return `ID${DIGITS(r, 9)}`;
  }
}
export function payerClaimNo(payer: string, r: () => number): string {
  const letters = payer.replace(/[^A-Za-z]/g, '').slice(0, 2).toUpperCase() || 'CL';
  return `${letters}-${DIGITS(r, 8)}`;
}

// ---------------------------------------------------------------- denials --

interface Denial {
  carc: string;
  text: string;
  /** Missing information gets a corrected claim; the rest fit an appeal. */
  corrected: boolean;
  attach: string;
}
export const DENIALS: Denial[] = [
  { carc: 'CARC 50', text: 'not medically necessary', corrected: false, attach: 'the clinical note and X-ray are attached' },
  { carc: 'CARC 119', text: 'the yearly frequency limit was reached', corrected: false, attach: 'the visit history showing the last claim is attached' },
  { carc: 'CARC 252', text: 'the payer asked for an X-ray', corrected: false, attach: 'the X-ray is attached' },
  { carc: 'CARC 204', text: 'a waiting period applies to this service', corrected: false, attach: 'the enrollment date from the eligibility check is attached' },
  { carc: 'CARC 16', text: 'the tooth surfaces were missing', corrected: true, attach: 'the surfaces were added from the clinical note' },
];
const APPEALABLE = DENIALS.filter((d) => !d.corrected);
const APPEAL_LOSABLE = APPEALABLE.filter((d) => d.carc !== 'CARC 252');
const CORRECTABLE = DENIALS.filter((d) => d.corrected);

// ---------------------------------------------------------------- life -----

interface Evt {
  day: number;
  ok: boolean;
  lead: string;
  detail?: string;
  who: string;
  bad?: boolean;
}
interface StepDef {
  label: string;
  days: number[];
  bad?: boolean;
  count?: number;
}

const AVA = 'Ava Thompson (admin)';

export class Life {
  evts: Evt[] = [];
  steps: StepDef[] = [];
  constructor(public visit: Date) {}
  at(n: number): Date {
    return addDays(this.visit, n);
  }
  d(n: number): string {
    return shortOf(this.at(n));
  }
  ev(day: number, lead: string, who: string, detail?: string, o: { ok?: boolean; bad?: boolean } = {}): void {
    this.evts.push({ day, ok: o.ok ?? true, lead, who, ...(detail ? { detail } : {}), ...(o.bad ? { bad: true } : {}) });
  }
  step(label: string, day: number, o: { bad?: boolean; count?: number; days?: number[] } = {}): void {
    this.steps.push({ label, days: o.days ?? [day], ...(o.bad ? { bad: true } : {}), ...(o.count ? { count: o.count } : {}) });
  }
  span(): number {
    return Math.max(0, ...this.evts.map((e) => e.day), ...this.steps.flatMap((s) => s.days));
  }
}

/** Everything a state plan returns. */
interface Built {
  life: Life;
  /** Days between the last event and today. */
  slack: number;
  status: ClaimStatus;
  where: string;
  tone: Tone;
  needsPerson: boolean;
  paid: number | null;
  allowed: number | null;
  owes: number;
  short?: number;
  expectedPay?: number;
  writtenOff?: { amount: number; reason: string };
  payments?: PaymentLine[];
  owesNote?: string;
  nextStep?: string;
  nextNote?: string;
  payerPaidDay?: number;
  depositDay?: number;
  closedDay?: number;
  openIn?: OpenLink;
  band: BandSentence;
  links?: ClaimLink[];
}

export type Variant =
  | 'default'
  | 'appeal-ready'
  | 'corrected-ready'
  | 'appeal-lost'
  | 'after-appeal'
  | 'limit-write-off'
  | 'not-appealed';

export interface BuildOpts {
  id: string;
  state: ClaimStatus | 'limit' | 'corrected';
  patient?: string;
  payer?: string;
  plan?: string;
  provider?: string;
  procedures?: HistoryProcedure[];
  memberId?: string;
  payerClaimNo?: string;
  /** Allowed ratio of the fee schedule (default 0.8 to 0.95 off the hash). */
  g?: number;
  variant?: Variant;
  /** Paid-short: the amount the payer actually paid. */
  paidShort?: number;
  /** Late: days since the payer accepted it. */
  lateDays?: number;
  denial?: Denial;
  /** A corrected claim replaces this one. */
  corrects?: { id: string; visit: Date; voidDay: number };
  /** The corrected claim's id when this one is replaced. */
  replacedBy?: string;
  /** Names to avoid so the generated set has no look-alike patients. */
  usedNames?: Set<string>;
}

const REJECTIONS = [
  { why: 'the subscriber ID did not match the plan', fix: `Corrected the subscriber ID in ${PMS} from the card on file · read back: matches` },
  { why: 'the rendering provider ID was missing', fix: `Added the dentist’s provider ID in ${PMS} · read back: matches` },
  { why: 'the patient’s birth date did not match the plan', fix: `Corrected the birth date in ${PMS} from the card on file · read back: matches` },
];
const HOLDS = ['Waiting for a document', 'Waiting for the pre-auth', 'Waiting for the new insurance card'];
const TAKE_BACKS = ['a duplicate payment', 'the payment went to the wrong claim', 'the plan ended before the visit'];
const NOT_BILLED = ['the patient paid in full at the visit', 'a no-charge redo under warranty', `the visit was cancelled in ${PMS}`];

const poss = (patient: string): string => `${patient.split(' ')[0] ?? patient}’s`;

/** The plan for a state: events at day offsets, money, labels. */
function plan(o: BuildOpts, r: () => number, visit: Date, procs: HistoryProcedure[], m: Money, patient: string, payer: string): Built {
  const L = new Life(visit);
  const P = poss(patient);
  const vS = shortOf(visit);
  const variant = o.variant ?? 'default';
  const ref = (p: string, n = 5): string => `${p}-${DIGITS(r, n)}`;

  // Shared prefix: prepared, confirmed, sent, accepted.
  const confirmDay = r() < 0.7 ? 0 : 1;
  const sentDay = confirmDay + (r() < 0.6 ? 0 : 1);
  const accDay = sentDay + 1;
  const prepared = (): void => {
    L.step('Prepared', 0);
    L.ev(0, 'Prepared the claim', 'ELVA', `from ${PMS}’s draft`);
  };
  const confirmed = (): void => {
    L.step('Confirmed', confirmDay);
    L.ev(confirmDay, 'Confirmed the claim', AVA);
  };
  const sent = (first = true): void => {
    L.step('Sent', sentDay);
    L.ev(sentDay, first ? `Sent it from ${PMS} · the clearinghouse accepted it` : `Sent it again from ${PMS} · the clearinghouse accepted it`, 'ELVA');
  };
  const accepted = (): void => {
    L.step('Accepted', accDay);
    L.ev(accDay, `${payer} accepted the claim`, 'ELVA');
  };
  const prefix = (): void => {
    prepared();
    confirmed();
    sent();
    accepted();
  };
  const base = { allowed: null as number | null, paid: null as number | null, owes: 0 };
  const waitingFor = `Waiting for ${payer}`;

  switch (o.state) {
    case 'waiting': {
      prepared();
      return {
        life: L, slack: int(r, 0, 2), status: 'waiting', where: 'Waiting for review', tone: 'amber', needsPerson: true, ...base,
        nextStep: 'Waiting for your review', nextNote: 'Claims to review',
        openIn: { label: 'Claims to review', to: '/claims', page: 'claims' },
        band: { pre: `${P} claim of ${vS} is prepared and `, em: 'waiting for your review', post: '.', mint: false },
      };
    }
    case 'on-hold': {
      const reason = pick(r, HOLDS);
      prepared();
      L.ev(1, 'Put the claim on hold', AVA, reason);
      const slack = int(r, 2, 12);
      const until = 1 + slack + int(r, 2, 9);
      return {
        life: L, slack, status: 'on-hold', where: `On hold · ${reason}`, tone: 'teal', needsPerson: false, ...base,
        nextStep: 'On hold', nextNote: `comes back ${L.d(until)}`,
        openIn: { label: 'Claims to review', to: '/claims', page: 'claims' },
        band: { pre: `${P} claim of ${vS} is on hold: ${reason.toLowerCase()}. `, em: `It comes back on ${L.d(until)}.`, mint: false },
      };
    }
    case 'sent': {
      prepared();
      confirmed();
      sent();
      return {
        life: L, slack: int(r, 0, 1), status: 'sent', where: `With ${payer}`, tone: 'teal', needsPerson: false, ...base,
        nextStep: waitingFor, nextNote: `usually by ${L.d(sentDay + 30)}`,
        openIn: { label: 'Insurance A/R', to: '/ar', page: 'ar' },
        band: { pre: `${P} claim of ${vS} was sent to ${payer}. The clearinghouse accepted it.`, mint: false },
      };
    }
    case 'rejected': {
      const rej = pick(r, REJECTIONS);
      prepared();
      confirmed();
      L.step('Sent', sentDay);
      L.ev(sentDay, `Sent it from ${PMS}`, 'ELVA');
      L.step('Rejected', sentDay + 1, { bad: true });
      L.ev(sentDay + 1, 'Read the clearinghouse report', 'ELVA', `${payer} rejected the claim before review, ${rej.why}`, { ok: false });
      L.ev(sentDay + 1, rej.fix, 'ELVA');
      return {
        life: L, slack: int(r, 0, 2), status: 'rejected', where: 'Rejected · ELVA fixed it, waiting for review', tone: 'amber', needsPerson: true, ...base,
        nextStep: 'Waiting for your review', nextNote: 'Claims to review',
        openIn: { label: 'Claims to review', to: '/claims', page: 'claims' },
        band: { pre: `${P} claim of ${vS} was rejected before review. ELVA fixed it. `, em: 'It needs your review.', mint: false },
      };
    }
    case 'with-payer': {
      prefix();
      return {
        life: L, slack: int(r, 0, 26), status: 'with-payer', where: `With ${payer}`, tone: 'teal', needsPerson: false, ...base,
        nextStep: waitingFor, nextNote: `usually by ${L.d(accDay + 30)}`,
        openIn: { label: 'Insurance A/R', to: '/ar', page: 'ar' },
        band: { pre: `${P} claim of ${vS} is with ${payer}.`, mint: false },
      };
    }
    case 'late': {
      prefix();
      const days = o.lateDays ?? int(r, 31, 55);
      // Late only past the payer's usual time (about 30 days after accepted).
      L.ev(accDay + 30, `Past ${payer}’s usual time`, 'ELVA', 'about 30 days after the claim was accepted');
      let last = 30;
      for (let k = 31, i = 0; k <= days; k += 7, i++) {
        if (i % 2 === 0) L.ev(accDay + k, `Checked ${payer}’s website: still in review`, 'ELVA');
        else L.ev(accDay + k, `Called ${payer}`, 'ELVA', `the claim is in review · reference ${ref('CL')}`);
        last = k;
      }
      return {
        life: L, slack: days - last, status: 'late', where: 'Late · ELVA following up', tone: 'teal', needsPerson: false, ...base,
        nextStep: waitingFor, nextNote: `next check ${L.d(accDay + last + 7)}`,
        openIn: { label: 'Insurance A/R', to: '/ar', page: 'ar' },
        band: { pre: `${P} claim of ${vS} is past ${payer}’s usual time. ELVA is following up.`, mint: false },
      };
    }
    case 'limit': {
      prefix();
      const days = o.lateDays ?? int(r, 68, 95);
      L.ev(accDay + 30, `Past ${payer}’s usual time`, 'ELVA', 'about 30 days after the claim was accepted');
      for (let k = 31, i = 0; k <= 66; k += 7, i++) {
        if (i % 2 === 0) L.ev(accDay + k, `Checked ${payer}’s website: still in review`, 'ELVA');
        else L.ev(accDay + k, `Called ${payer}`, 'ELVA', `the claim is in review · reference ${ref('CL')}`);
      }
      L.ev(accDay + 67, 'Reached the follow-up limit', 'ELVA', `6 checks and ${payer} has not answered`, { ok: false });
      return {
        life: L, slack: days - 67, status: 'late', where: 'Needs a person · follow-up limit', tone: 'amber', needsPerson: true, ...base,
        nextStep: 'Waiting for you', nextNote: 'follow-up limit reached',
        openIn: { label: 'Insurance A/R', to: '/ar', page: 'ar' },
        band: { pre: `${P} claim of ${vS} reached ELVA’s follow-up limit at ${payer}. `, em: 'It needs a person.', mint: false },
      };
    }
    case 'paid-short': {
      prefix();
      const payDay = accDay + int(r, 14, 24);
      const paid = o.paidShort ?? Math.round(m.ins * (0.55 + r() * 0.25));
      const short = m.ins - paid;
      L.step('Paid', payDay);
      L.ev(payDay, `Read the payment: ${payer} paid ${usd(paid)} of ${usd(m.ins)} expected, ${usd(short)} short`, 'ELVA');
      L.ev(payDay, `Asked ${payer} to reprocess`, 'ELVA', r() < 0.5 ? `called, reference ${ref('RP')}` : `on ${payer}’s website, reference ${ref('RP')}`);
      const slack = int(r, 1, 18);
      return {
        life: L, slack, status: 'paid-short', where: 'Paid short · ELVA asked to reprocess', tone: 'teal', needsPerson: false,
        allowed: m.allowed, paid, owes: m.own, short, expectedPay: m.ins, payerPaidDay: payDay,
        owesNote: `The ${usd(short)} short is not billed to the patient while ELVA asks ${payer} to reprocess.`,
        nextStep: waitingFor, nextNote: `next check ${L.d(payDay + slack + int(r, 2, 6))}`,
        openIn: { label: 'Denied & underpaid', to: '/denied', page: 'denied' },
        band: { pre: `${payer} paid ${usd(paid)} of the ${usd(m.ins)} expected on ${P} claim. `, em: `ELVA asked ${payer} to reprocess.`, mint: false },
      };
    }
    case 'written-off': {
      const kind: Variant = variant !== 'default' ? variant : (() => { const x = r(); return x < 0.5 ? 'appeal-lost' : x < 0.8 ? 'limit-write-off' : 'not-appealed'; })();
      const dBase = { allowed: 0, paid: null as number | null, owes: 0 };
      prefix();
      if (kind === 'limit-write-off') {
        L.ev(accDay + 30, `Past ${payer}’s usual time`, 'ELVA', 'about 30 days after the claim was accepted');
        for (let k = 31, i = 0; k <= 66; k += 7, i++) {
          if (i % 2 === 0) L.ev(accDay + k, `Checked ${payer}’s website: still in review`, 'ELVA');
          else L.ev(accDay + k, `Called ${payer}`, 'ELVA', `the claim is in review · reference ${ref('CL')}`);
        }
        L.ev(accDay + 67, 'Reached the follow-up limit', 'ELVA', `6 checks and ${payer} has not answered`, { ok: false });
        const woDay = accDay + 67 + int(r, 3, 14);
        L.step('Written off', woDay);
        L.ev(woDay, 'Wrote the claim off', AVA, 'no answer from the payer after the follow-up limit');
        const reason = `no answer from ${payer}`;
        return {
          life: L, slack: int(r, 0, 500), status: 'written-off', where: `Written off · ${reason}`, tone: 'grey', needsPerson: false, ...dBase,
          writtenOff: { amount: m.billed, reason }, closedDay: woDay,
          band: { pre: `${P} claim of ${vS} was written off: ${payer} never answered.`, mint: false },
        };
      }
      const den = o.denial && !o.denial.corrected ? o.denial : pick(r, APPEAL_LOSABLE);
      const denDay = accDay + int(r, 14, 24);
      L.step('Denied', denDay, { bad: true });
      L.ev(denDay, `Read ${payer}’s ERA`, 'ELVA', `denied · ${den.carc}, ${den.text}`, { ok: false });
      if (kind === 'not-appealed') {
        const woDay = denDay + int(r, 3, 12);
        L.step('Written off', woDay);
        L.ev(woDay, 'Decided not to appeal', AVA, den.text);
        L.ev(woDay, 'Wrote the claim off', AVA, 'the denial stands');
        const reason = den.text;
        return {
          life: L, slack: int(r, 0, 500), status: 'written-off', where: `Written off · ${reason}`, tone: 'grey', needsPerson: false, ...dBase,
          writtenOff: { amount: m.billed, reason }, closedDay: woDay,
          band: { pre: `${P} claim of ${vS} was written off: ${den.text}.`, mint: false },
        };
      }
      L.ev(denDay, 'Prepared an appeal', 'ELVA', den.attach);
      const apDay = denDay + int(r, 1, 3);
      L.step('Appealed', apDay);
      L.ev(apDay, 'Sent the appeal', 'ELVA', `on ${payer}’s website · ${payer} confirmed receipt, reference ${ref('AP')}`);
      const lostDay = apDay + int(r, 28, 40);
      L.ev(lostDay, `Read ${payer}’s answer`, 'ELVA', `appeal denied · ${den.text}`, { ok: false });
      const woDay = lostDay + int(r, 2, 10);
      L.step('Written off', woDay);
      L.ev(woDay, 'Wrote the claim off', AVA, 'the appeal was denied and nothing more can be tried');
      const reason = 'appeal denied';
      return {
        life: L, slack: int(r, 0, 500), status: 'written-off', where: `Written off · ${reason}`, tone: 'grey', needsPerson: false, ...dBase,
        writtenOff: { amount: m.billed, reason }, closedDay: woDay,
        band: { pre: `${P} claim of ${vS} was written off: the appeal was denied.`, mint: false },
      };
    }
    case 'denied':
    case 'appealed': {
      prefix();
      const wantCorrected = variant === 'corrected-ready';
      const den = o.denial ?? (o.state === 'appealed' || variant === 'appeal-ready' ? pick(r, APPEALABLE) : wantCorrected ? pick(r, CORRECTABLE) : r() < 0.7 ? pick(r, APPEALABLE) : pick(r, CORRECTABLE));
      const denDay = accDay + int(r, 14, 24);
      L.step('Denied', denDay, { bad: true });
      L.ev(denDay, `Read ${payer}’s ERA`, 'ELVA', `denied · ${den.carc}, ${den.text}`, { ok: false });
      const dBase = { allowed: 0, paid: null as number | null, owes: 0 };
      if (o.state === 'denied') {
        if (den.corrected) L.ev(denDay, 'Prepared a corrected claim', 'ELVA', den.attach);
        else L.ev(denDay, 'Prepared an appeal', 'ELVA', den.attach);
        return {
          life: L, slack: int(r, 0, 14), status: 'denied', where: den.corrected ? 'Denied · corrected claim ready' : 'Denied · appeal ready', tone: 'red', needsPerson: true, ...dBase,
          nextStep: 'Waiting for you', nextNote: den.corrected ? 'corrected claim ready' : 'appeal ready',
          openIn: { label: 'Denied & underpaid', to: '/denied', page: 'denied' },
          band: { pre: `${P} claim of ${vS} was denied. `, em: den.corrected ? 'The corrected claim is ready for you to send.' : 'The appeal is ready for you to send.', mint: false },
        };
      }
      L.ev(denDay, 'Prepared an appeal', 'ELVA', den.attach);
      const apDay = denDay + int(r, 1, 3);
      L.step('Appealed', apDay);
      L.ev(apDay, 'Sent the appeal', 'ELVA', `on ${payer}’s website · ${payer} confirmed receipt, reference ${ref('AP')}`);
      return {
        life: L, slack: int(r, 0, 30), status: 'appealed', where: 'Appeal sent', tone: 'teal', needsPerson: false, ...dBase,
        nextStep: waitingFor, nextNote: `usually by ${L.d(apDay + 35)}`,
        openIn: { label: 'Denied & underpaid', to: '/denied/appeals', page: 'denied' },
        band: { pre: `${P} claim of ${vS} was denied. The appeal is with ${payer}.`, mint: false },
      };
    }
    case 'paid':
    case 'posted':
    case 'deposited':
    case 'taken-back': {
      prefix();
      const afterAppeal = variant === 'after-appeal';
      let payDay: number;
      if (afterAppeal) {
        const den = pick(r, APPEALABLE);
        const denDay = accDay + int(r, 14, 24);
        L.step('Denied', denDay, { bad: true });
        L.ev(denDay, `Read ${payer}’s ERA`, 'ELVA', `denied · ${den.carc}, ${den.text}`, { ok: false });
        L.ev(denDay, 'Prepared an appeal', 'ELVA', den.attach);
        const apDay = denDay + int(r, 1, 3);
        L.step('Appealed', apDay);
        L.ev(apDay, 'Sent the appeal', 'ELVA', `on ${payer}’s website · ${payer} confirmed receipt, reference ${ref('AP')}`);
        payDay = apDay + int(r, 24, 35);
        L.step('Paid', payDay);
        L.ev(payDay, `Read the payment: ${payer} paid ${usd(m.ins)} after the appeal was approved`, 'ELVA');
      } else {
        payDay = accDay + int(r, 14, 24);
        L.step('Paid', payDay);
        L.ev(payDay, `Read the payment: ${payer} paid ${usd(m.ins)}`, 'ELVA');
      }
      const money = { allowed: m.allowed, paid: m.ins, owes: m.own };
      if (o.state === 'paid') {
        return {
          life: L, slack: int(r, 0, 1), status: 'paid', where: 'Paid · to post', tone: 'green', needsPerson: false, ...money, payerPaidDay: payDay,
          nextStep: 'Waiting to post', nextNote: 'Posting',
          openIn: { label: 'Posting', to: '/posting', page: 'posting' },
          band: { pre: `${P} claim of ${vS} was `, em: 'paid', post: `. ${payer} paid ${usd(m.ins)} and ELVA will post it.`, mint: true },
        };
      }
      const postDay = payDay + 1;
      L.step('Posted', postDay);
      L.ev(postDay, `Posted ${usd(m.ins)} to ${PMS}`, 'ELVA', `Patient owes ${usd(m.own)}`);
      if (o.state === 'posted') {
        return {
          life: L, slack: int(r, 0, 4), status: 'posted', where: 'Posted · waiting for the deposit', tone: 'green', needsPerson: false, ...money, payerPaidDay: payDay,
          nextStep: 'Waiting for the deposit', nextNote: `usually by ${L.d(postDay + 5)}`,
          openIn: { label: 'Posting', to: '/posting/posted', page: 'posting' },
          band: { pre: `${P} claim of ${vS} was paid and posted. ELVA is waiting for the deposit.`, mint: false },
        };
      }
      const depDay = postDay + int(r, 3, 5);
      L.step('Deposited', depDay);
      L.ev(depDay, 'Matched the deposit', 'ELVA', `${payer} EFT`);
      const owesBit = m.own > 0 ? ` The patient owes ${usd(m.own)}.` : '';
      if (o.state === 'deposited') {
        const span = L.span();
        return {
          life: L, slack: int(r, 0, Math.max(0, 700 - span)), status: 'deposited', where: afterAppeal ? 'Deposited · after an appeal' : 'Deposited', tone: 'green', needsPerson: false, ...money,
          payerPaidDay: payDay, depositDay: depDay, closedDay: depDay,
          band: afterAppeal
            ? { pre: `${P} claim of ${vS} was paid `, em: 'after an appeal', post: `. ${payer} paid ${usd(m.ins)}.${owesBit}`, mint: true }
            : { pre: `${P} claim of ${vS} was `, em: 'paid', post: `. ${payer} paid ${usd(m.ins)}.${owesBit}`, mint: true },
        };
      }
      // taken back
      const tb = Math.max(20, Math.round(m.ins * 0.25));
      const tbDay = depDay + int(r, 10, 40);
      const why = pick(r, TAKE_BACKS);
      L.step('Taken back', tbDay, { bad: true });
      L.ev(tbDay, `${payer} took back ${usd(tb)} on ${L.d(tbDay)}`, 'ELVA', `the ERA says ${why}`, { ok: false, bad: true });
      L.ev(tbDay, 'Opened the claim again', 'ELVA', `in Insurance A/R · ELVA is asking ${payer} why`);
      L.ev(tbDay + 1, `Asked ${payer} why`, 'ELVA', `called, reference ${ref('TB')}`);
      const slack = int(r, 1, 14);
      return {
        life: L, slack, status: 'taken-back', where: 'Taken back · ELVA following up', tone: 'red', needsPerson: false,
        allowed: m.allowed, paid: m.ins - tb, owes: m.own, payerPaidDay: payDay, depositDay: depDay,
        payments: [{ amount: m.ins, date: L.d(payDay) }, { amount: -tb, date: L.d(tbDay) }],
        owesNote: `The ${usd(tb)} taken back is not billed to the patient while the claim is open.`,
        nextStep: waitingFor, nextNote: `next check ${L.d(tbDay + 1 + slack + int(r, 2, 6))}`,
        openIn: { label: 'Insurance A/R', to: '/ar', page: 'ar' },
        band: { pre: `${payer} took back ${usd(tb)} on ${P} claim of ${vS}. `, em: `It’s open again; ELVA is asking ${payer} why.`, mint: false },
      };
    }
    case 'not-billed': {
      prepared();
      const why = pick(r, NOT_BILLED);
      L.step('Not billed', 1);
      L.ev(1, 'Marked the claim not billed', AVA, why);
      return {
        life: L, slack: int(r, 2, 400), status: 'not-billed', where: 'Not billed', tone: 'grey', needsPerson: false, ...base, closedDay: 1,
        band: { pre: `${P} claim of ${vS} was not billed: ${why}.`, mint: false },
      };
    }
    case 'replaced': {
      prefix();
      const voidDay = accDay + int(r, 3, 9);
      const tooth = procs[0]?.tooth ?? '#18';
      L.step('Replaced', voidDay);
      L.ev(voidDay, 'Voided the claim', 'ELVA', `the crown was on tooth ${tooth}, not the tooth first billed. Replaced by ${o.replacedBy ?? 'a corrected claim'}`);
      return {
        life: L, slack: int(r, 2, 22), status: 'replaced', where: `Replaced by ${o.replacedBy ?? 'a corrected claim'}`, tone: 'grey', needsPerson: false, ...base, closedDay: voidDay,
        ...(o.replacedBy ? { links: [{ label: 'Replaced by', id: o.replacedBy }] } : {}),
        band: { pre: `${P} claim of ${vS} had the wrong details. It was `, em: 'replaced by a corrected claim', post: '.', mint: false },
      };
    }
    case 'corrected': {
      const c = o.corrects;
      const vd = c?.voidDay ?? 0;
      L.step('Prepared', vd);
      L.ev(vd, 'Prepared the corrected claim', 'ELVA', `replacing ${c?.id ?? 'the first claim'}`);
      L.step('Confirmed', vd);
      L.ev(vd, 'Confirmed the claim', AVA);
      L.step('Sent', vd + 1);
      L.ev(vd + 1, `Sent it from ${PMS} · the clearinghouse accepted it`, 'ELVA', 'marked as a replacement');
      L.step('Accepted', vd + 2);
      L.ev(vd + 2, `${payer} accepted the claim`, 'ELVA');
      return {
        life: L, slack: 0, status: 'with-payer', where: `With ${payer} · corrected claim`, tone: 'teal', needsPerson: false, ...base,
        nextStep: waitingFor, nextNote: `usually by ${L.d(vd + 2 + 30)}`,
        openIn: { label: 'Insurance A/R', to: '/ar', page: 'ar' },
        ...(c ? { links: [{ label: 'Replaces', id: c.id }] } : {}),
        band: { pre: `${P} corrected claim is with ${payer}. It `, em: `replaces ${c?.id ?? 'the first claim'}`, post: '.', mint: false },
      };
    }
    default:
      throw new Error(`unknown state ${o.state}`);
  }
}

// ---------------------------------------------------------------- build ----

function isWeekend(dt: Date): boolean {
  return dt.getDay() === 0 || dt.getDay() === 6;
}

/** Plans (twice: once to measure, once placed) and assembles the record. */
interface BuildResult {
  claim: HistoryClaim;
  visit: Date;
  closedDay: number;
}

function build(o: BuildOpts): BuildResult {
  const seed = hashSeed(o.id);
  const head = rng(seed ^ 0x9e3779b9);
  let patient = o.patient ?? '';
  if (!patient) {
    for (let i = 0; i < 20; i++) {
      patient = `${pick(head, FIRSTS)} ${pick(head, LAST_NAMES)}`;
      if (!o.usedNames?.has(patient)) break;
    }
  }
  const payer = o.payer ?? pick(head, ['Aetna', 'Delta Dental', 'Cigna', 'MetLife', 'UnitedHealthcare', 'Guardian', 'Sun Life', 'BCBS of Texas', 'Humana']);
  const plan_ = o.plan ?? `${payer} ${pick(head, PLAN_SUFFIX)}`;
  const provider = o.provider ?? pick(head, PROVIDERS);
  const procedures = o.procedures ?? procsFor(head);
  const g = o.g ?? 0.8 + head() * 0.15;
  const m = moneyOf(procedures, g);
  const memberId = o.memberId ?? memberIdFor(payer, head);
  const pcn = o.payerClaimNo ?? payerClaimNo(payer, head);

  const run = (visit: Date): Built => plan(o, rng(seed), visit, procedures, m, patient, payer);
  let visit: Date;
  if (o.state === 'corrected' && o.corrects) {
    visit = o.corrects.visit;
  } else {
    const measured = run(CLAIMS_TODAY);
    visit = addDays(CLAIMS_TODAY, -(measured.life.span() + measured.slack));
    while (isWeekend(visit)) visit = addDays(visit, -1);
  }
  const b = run(visit);
  const L = b.life;

  // Stamps: spread the clock by a hash of the id, keep each day in order.
  const evts = [...L.evts].sort((a, c) => a.day - c.day);
  let prevDay = Number.NaN;
  let prevMin = 0;
  const timeline: TimelineEntry[] = evts.map((e, i) => {
    const dt = L.at(e.day);
    let min = 6 * 60 + (hashSeed(`${o.id}|${String(i)}`) % (12 * 60));
    if (e.day === prevDay && min <= prevMin) min = prevMin + 3 + (hashSeed(`${o.id}|t${String(i)}`) % 40);
    const isToday = isoOf(dt) === isoOf(CLAIMS_TODAY);
    if (isToday && min > CLAIMS_TODAY_MAX_MIN && e.day !== prevDay) min = 6 * 60 + (hashSeed(`${o.id}|${String(i)}`) % (5 * 60));
    min = Math.min(min, 23 * 60 + 58);
    prevDay = e.day;
    prevMin = min;
    return {
      stamp: `${shortOf(dt)}, ${clock12(min)}`,
      iso: isoOf(dt),
      ok: e.ok,
      lead: e.lead,
      who: e.who,
      ...(e.detail ? { detail: e.detail } : {}),
      ...(e.bad ? { bad: true } : {}),
    };
  });

  const path: PathStep[] = L.steps.map((s) => ({
    label: s.label,
    date: s.days.map((d) => L.d(d)).join(' · '),
    ...(s.bad ? { bad: true } : {}),
    ...(s.count ? { count: s.count } : {}),
  }));

  const last = evts[evts.length - 1];
  const lastDt = L.at(last?.day ?? 0);
  const claim: HistoryClaim = {
    id: o.id,
    patient,
    payer,
    plan: plan_,
    payerClaimNo: pcn,
    billed: m.billed,
    paid: b.paid,
    allowed: b.allowed,
    expectedAllowed: m.allowed,
    patientOwes: b.owes,
    writeOff: b.allowed != null && b.allowed > 0 ? m.writeOff : null,
    ...(b.payments ? { payments: b.payments } : {}),
    ...(b.owesNote ? { owesNote: b.owesNote } : {}),
    ...(b.short != null ? { short: b.short } : {}),
    ...(b.expectedPay != null ? { expectedPay: b.expectedPay } : {}),
    ...(b.writtenOff ? { writtenOff: b.writtenOff } : {}),
    ...(b.links ? { links: b.links } : {}),
    visitIso: isoOf(visit),
    visitShort: shortOf(visit),
    visitFull: fullOf(visit),
    lastChangeIso: isoOf(lastDt),
    lastChangeLabel: shortOf(lastDt),
    status: b.status,
    tone: b.tone,
    where: b.where,
    needsPerson: b.needsPerson,
    memberId,
    provider,
    codes: [...new Set(procedures.map((p) => p.code))],
    procedures,
    path,
    ...(b.nextStep ? { nextStep: b.nextStep } : {}),
    ...(b.nextNote ? { nextNote: b.nextNote } : {}),
    timeline,
    ...(b.payerPaidDay != null ? { payerPaidDate: L.d(b.payerPaidDay) } : {}),
    ...(b.depositDay != null ? { depositDate: L.d(b.depositDay) } : {}),
    ...(b.closedDay != null ? { closedDate: L.d(b.closedDay) } : {}),
    ...(b.openIn ? { openIn: b.openIn } : {}),
    band: b.band,
  };
  return { claim, visit, closedDay: b.closedDay ?? 0 };
}

export function buildClaim(o: BuildOpts): HistoryClaim {
  return build(o).claim;
}

/** A replaced claim and the corrected claim that replaces it (linked both ways). */
export function buildReplacementPair(oldId: string, newId: string, base: Pick<BuildOpts, 'patient' | 'payer' | 'usedNames'> = {}): [HistoryClaim, HistoryClaim] {
  const head = rng(hashSeed(oldId) ^ 0x51ed270b);
  const tooth = pick(head, MOLARS);
  const wrong = Number(tooth.slice(1));
  const fixedTooth = `#${String(wrong >= 31 ? wrong - 1 : wrong + 1)}`;
  const old = build({ ...base, id: oldId, state: 'replaced', replacedBy: newId, procedures: [proc('D2740', tooth), proc('D2950', tooth)] });
  const fresh = build({
    ...base,
    id: newId,
    state: 'corrected',
    patient: old.claim.patient,
    payer: old.claim.payer,
    plan: old.claim.plan,
    provider: old.claim.provider,
    memberId: old.claim.memberId,
    procedures: [proc('D2740', fixedTooth), proc('D2950', fixedTooth)],
    corrects: { id: oldId, visit: old.visit, voidDay: old.closedDay },
  });
  return [old.claim, fresh.claim];
}
